/**
 * Keeps the offer picker and the buy-now button in sync, and reveals the
 * sticky bar once the main button scrolls out of view.
 *
 * Everything here is an enhancement: the picker is native radio inputs and the
 * button renders the default tier's price server-side, so the page still sells
 * with this file blocked.
 */
(function () {
  if (window.fkPickerReady) return;
  window.fkPickerReady = true;

  const PICKER_INPUT = 'variant-picker input[data-variant-id]';

  // Microsoft Clarity tagging (no-op when the Clarity app block is absent).
  // "set" tags stick to the session so recordings and heatmaps can be filtered
  // by tier / CTA placement; "event" marks the moment in the recording.
  function track(name, tags) {
    if (typeof window.clarity !== 'function') return;
    try {
      Object.keys(tags || {}).forEach(function (key) {
        window.clarity('set', key, String(tags[key]));
      });
      window.clarity('event', name);
    } catch (e) {}
  }

  // The card the visitor touched last, anywhere on the page. A button in a
  // section without its own picker (the closing CTA) charges that card, so
  // the page never sells one tier above and another below.
  let lastPicked = null;

  function selectedInput(button) {
    const scope = button.closest('.shopify-section') || document;
    const local = scope.querySelector(PICKER_INPUT + ':checked');
    if (local) return local;
    if (lastPicked && lastPicked.checked) return lastPicked;
    return null;
  }

  function selectedVariant(button) {
    const checked = selectedInput(button);
    return checked ? checked.dataset.variantId : button.dataset.variantId;
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-fk-buy-now]');
    if (!button) return;
    const id = selectedVariant(button);
    if (!id) return;
    button.setAttribute('aria-busy', 'true');
    const checked = selectedInput(button);
    track('buy_now_click', {
      tier: checked ? checked.value : 'default',
      variant_id: id,
      cta_placement: button.closest('[data-fk-buy-now-sticky]') ? 'sticky' : 'main',
    });
    window.location.href = '/cart/' + id + ':1';
  });

  document.addEventListener('change', function (event) {
    const input = event.target.closest(PICKER_INPUT);
    if (!input || !input.dataset.fkPrice) return;
    lastPicked = input;
    const scope = input.closest('.shopify-section') || document;
    const available = input.dataset.optionAvailable !== 'false';
    function follow(root) {
      root.querySelectorAll('[data-fk-buy-now-price]').forEach(function (el) {
        el.textContent = input.dataset.fkPrice;
      });
      root.querySelectorAll('[data-fk-buy-now-tier]').forEach(function (el) {
        el.textContent = input.value;
      });
      // A sold-out card must never reach checkout or the cart.
      root.querySelectorAll('[data-fk-buy-now], [data-fk-add-to-cart]').forEach(function (button) {
        button.disabled = !available;
        button.dataset.variantId = input.dataset.variantId || button.dataset.variantId;
      });
    }
    follow(scope);
    // Buttons in sections that have no picker of their own follow as well.
    document.querySelectorAll('.shopify-section').forEach(function (section) {
      if (section === scope || section.querySelector(PICKER_INPUT)) return;
      follow(section);
    });
    track('tier_change', { tier: input.value });
  });

  // Sticky "Add to cart": adds the selected variant through the Ajax cart API,
  // then opens the theme's cart drawer (through the same CartLinesUpdateEvent
  // Horizon's own product form dispatches, so the drawer, its line items and
  // the header bubble all refresh themselves). Never navigates to checkout.
  // Without a drawer on the page it routes to the cart page after the add.
  // One request per click: a second click while one is in flight is ignored.
  let cartEventsModule = null;
  function cartEvents() {
    if (cartEventsModule) return cartEventsModule;
    cartEventsModule = import('@shopify/events').catch(function () {
      return null;
    });
    return cartEventsModule;
  }

  function setStatus(bar, text, state) {
    const status = bar ? bar.querySelector('[data-fk-cart-status]') : null;
    if (!status) return;
    status.textContent = text || '';
    if (state) status.dataset.state = state;
    else delete status.dataset.state;
  }

  function setLabel(button, key) {
    const label = button.querySelector('.fk-buy-now__label');
    const text = button.dataset['label' + key];
    if (label && text) label.textContent = text;
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-fk-add-to-cart]');
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    const id = selectedVariant(button);
    if (!id) return;
    const bar = button.closest('[data-fk-buy-now-sticky]');
    const routes = (window.Theme && window.Theme.routes) || {};
    const addUrl = routes.cart_add_url || '/cart/add.js';
    const cartUrl = routes.cart_url || '/cart';
    const drawer = document.querySelector('cart-drawer-component');
    const checked = selectedInput(button);

    button.setAttribute('aria-busy', 'true');
    setLabel(button, 'Adding');
    setStatus(bar, '', null);
    track('add_to_cart_click', {
      tier: checked ? checked.value : 'default',
      variant_id: id,
      cta_placement: 'sticky',
    });

    const sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      sectionIds.push(el.dataset.sectionId);
    });

    cartEvents().then(function (events) {
      let resolveEvent = null;
      let rejectEvent = null;
      if (events && events.CartLinesUpdateEvent && drawer) {
        const deferred = new Promise(function (resolve, reject) {
          resolveEvent = resolve;
          rejectEvent = reject;
        });
        // Prevents an unhandled rejection in the console when the add fails
        // and the drawer's own catch runs later.
        deferred.catch(function () {});
        try {
          drawer.dispatchEvent(
            new events.CartLinesUpdateEvent({
              action: 'add',
              context: 'product',
              lines: [{ merchandiseId: String(id), quantity: 1 }],
              promise: deferred,
            })
          );
        } catch (e) {
          resolveEvent = null;
        }
      }

      return fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections: sectionIds.join(',') }),
      })
        .then(function (response) {
          return response.json().then(function (body) {
            return { ok: response.ok, body: body };
          });
        })
        .then(function (result) {
          if (!result.ok || result.body.status) {
            const message = result.body.description || result.body.message || button.dataset.labelError;
            if (resolveEvent) {
              // Settle the drawer's promise with the real cart so nothing hangs.
              fetch(cartUrl + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
                .then(function (r) {
                  return r.json();
                })
                .then(function (cart) {
                  resolveEvent({
                    cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                    detail: { didError: true, items: cart.items, source: 'fk-sticky-cart', itemCount: cart.item_count },
                  });
                })
                .catch(rejectEvent);
            }
            throw new Error(message);
          }
          return fetch(cartUrl + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then(function (r) {
              return r.json();
            })
            .then(function (cart) {
              if (resolveEvent) {
                resolveEvent({
                  cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                  detail: {
                    didError: false,
                    items: cart.items,
                    source: 'fk-sticky-cart',
                    itemCount: cart.item_count,
                    sections: result.body.sections,
                  },
                });
              }
              setLabel(button, 'Added');
              setStatus(bar, button.dataset.labelAdded, 'added');
              button.removeAttribute('aria-busy');
              if (!resolveEvent) {
                // No drawer to open: the cart page is the acknowledgement.
                window.location.href = cartUrl;
                return;
              }
              setTimeout(function () {
                setLabel(button, 'Idle');
                setStatus(bar, '', null);
              }, 2500);
            });
        })
        .catch(function (error) {
          button.removeAttribute('aria-busy');
          setLabel(button, 'Idle');
          setStatus(bar, (error && error.message) || button.dataset.labelError, 'error');
        });
    });
  });

  // Gallery videos flagged data-fk-autoplay start, muted, once their slide is
  // on screen, and pause when it leaves. The file is not fetched before that,
  // so the first image keeps the connection to itself.
  function watchAutoplayVideos() {
    const medias = document.querySelectorAll('deferred-media[data-fk-autoplay]');
    if (!medias.length || !('IntersectionObserver' in window)) return;

    function videoOf(media) {
      return media.querySelector('video');
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          const media = entry.target;
          if (entry.isIntersecting) {
            if (!media.getAttribute('data-media-loaded') && typeof media.loadContent === 'function') {
              media.loadContent(false);
            }
            const video = videoOf(media);
            if (!video) return;
            video.muted = true;
            video.setAttribute('muted', '');
            video.setAttribute('playsinline', '');
            video.setAttribute('loop', '');
            const playing = video.play();
            if (playing && playing.catch) playing.catch(function () {});
          } else {
            const video = videoOf(media);
            if (video && !video.paused) video.pause();
          }
        });
      },
      { threshold: 0.6 }
    );

    medias.forEach(function (media) {
      // The poster button is for visitors without autoplay; the video is the
      // control here, so the play glyph only confuses.
      const poster = media.querySelector('.deferred-media__poster-icon');
      if (poster) poster.hidden = true;
      observer.observe(media);
    });
  }

  // Deferred images (gallery slides after the first, UGC cards) ship a 32px
  // placeholder with the real srcset in data attributes, so nothing competes
  // with the hero image for the connection. They are swapped in:
  //   - gallery slides: as soon as the hero image has loaded, all of them,
  //     so a fast swipe never lands on a blank slide;
  //   - cards flagged data-fk-near: when they come within 300px of the viewport.
  // On a phone the candidates stop at 832px, as they do for the hero: at 3x a
  // slide would otherwise cost 250-300 KB each for detail no one sees.
  function mobileSrcset(srcset) {
    if (window.innerWidth >= 750) return srcset;
    return srcset
      .split(',')
      .filter(function (candidate) {
        const match = candidate.trim().match(/\s(\d+)w$/);
        return !match || Number(match[1]) <= 832;
      })
      .join(',');
  }

  function swapDeferredImage(img) {
    if (!img.dataset.fkSrcset) return;
    img.sizes = img.dataset.fkSizes || '100vw';
    img.srcset = mobileSrcset(img.dataset.fkSrcset);
    img.loading = 'eager';
    img.removeAttribute('data-fk-srcset');
    img.removeAttribute('data-fk-sizes');
    function reveal() {
      img.classList.remove('fk-deferred-image');
    }
    img.addEventListener('load', reveal, { once: true });
    img.addEventListener('error', reveal, { once: true });
    // Whatever happens, no slide stays blurred.
    setTimeout(reveal, 5000);
  }

  function watchDeferredImages() {
    const slides = Array.prototype.slice.call(
      document.querySelectorAll('media-gallery img[data-fk-srcset]:not([data-fk-near])')
    );
    const near = Array.prototype.slice.call(document.querySelectorAll('img[data-fk-srcset][data-fk-near]'));

    // The two slides next to the hero load once the page has painted and the
    // browser is idle, so the first swipes land on finished pictures. They
    // used to load on the hero's `load` event, but the hero is preloaded at
    // high priority and finishes before the render-blocking stylesheet on a
    // slow connection, so 200 KB of pictures nobody could see yet were
    // competing with the CSS for the first paint. Now they wait for the
    // hero, the stylesheet (window `load` covers both) or the visitor's first
    // interaction, whichever comes first, and then for an idle slot.
    // The rest wait for the visitor's first interaction anywhere on the page:
    // a touch, a click, a scroll, a key, a wheel. Nobody reaches slide 4
    // without one of those, and a swipe takes longer than an 80 KB download.
    // A visitor who never interacts never pays for five pictures they never see.
    const nextSlides = slides.slice(0, 2);
    const restSlides = slides.slice(2);
    let nextLoaded = false;

    function loadNext() {
      if (nextLoaded) return;
      nextLoaded = true;
      window.requestIdleCallback(
        function () {
          nextSlides.forEach(swapDeferredImage);
        },
        { timeout: 1000 }
      );
    }
    function loadRest() {
      loadNext();
      restSlides.forEach(swapDeferredImage);
    }
    if (slides.length) {
      if (document.readyState === 'complete') {
        loadNext();
      } else {
        window.addEventListener('load', loadNext, { once: true });
        // A load event that never fires (a hung third-party script) must not
        // hold the gallery hostage.
        setTimeout(loadNext, 4000);
      }
      ['touchstart', 'pointerdown', 'mousedown', 'keydown', 'wheel', 'scroll'].forEach(function (type) {
        window.addEventListener(type, loadRest, { once: true, passive: true, capture: true });
      });
    }

    if (near.length) {
      if (!('IntersectionObserver' in window)) {
        near.forEach(swapDeferredImage);
        return;
      }
      const observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            swapDeferredImage(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '300px 0px' }
      );
      near.forEach(function (img) {
        observer.observe(img);
      });
    }
  }

  function watchStickyBars() {
    if (!('IntersectionObserver' in window)) return;
    document.querySelectorAll('[data-fk-buy-now-sticky]').forEach(function (bar) {
      if (bar.dataset.fkObserved === 'true') return;
      const previous = bar.previousElementSibling;
      const main = previous ? previous.querySelector('[data-fk-buy-now]') : null;
      if (!main) return;
      bar.dataset.fkObserved = 'true';
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
          });
        },
        { threshold: 0 }
      ).observe(main);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchStickyBars);
  } else {
    watchStickyBars();
    watchAutoplayVideos();
    watchDeferredImages();
  }

  document.addEventListener('shopify:section:load', watchStickyBars);
})();
